import Logout from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import Refresh from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import LightMode from '@mui/icons-material/LightMode';
import DarkMode from '@mui/icons-material/DarkMode';
import SettingsBrightness from '@mui/icons-material/SettingsBrightness';
import LanguageIcon from '@mui/icons-material/Language';
import SearchIcon from '@mui/icons-material/Search';
import { USER_PREF_KEYS } from '@/services/userPreferencesService';
import { SUPPORTED_LANGUAGES } from '@/i18n';
import type { SlashCommand } from './types';

/**
 * Static utility commands that aren't derived from the navigation config.
 * Kept intentionally small and opinionated.
 */
export function buildUtilityCommands(): SlashCommand[] {
  const commands: SlashCommand[] = [
    {
      id: 'util.logout',
      title: { key: 'common.logout', value: 'Logout' },
      group: 'utilities',
      keywords: ['logout', 'sign out', 'signout', 'exit'],
      localizedKeywordsKey: 'slashCommand.keywords.util.logout',
      icon: Logout,
      run: async ({ logout, closeDialog }) => {
        closeDialog();
        await logout();
      },
    },
    {
      id: 'util.settings',
      title: { key: 'slashCommand.util.settings', value: 'User Settings' },
      group: 'utilities',
      keywords: ['settings', 'preferences', 'profile'],
      localizedKeywordsKey: 'slashCommand.keywords.util.settings',
      icon: SettingsIcon,
      run: ({ navigate, closeDialog }) => {
        closeDialog();
        navigate('/user/settings');
      },
    },
    {
      id: 'util.reload',
      title: { key: 'slashCommand.util.reload', value: 'Reload page' },
      group: 'utilities',
      keywords: ['reload', 'refresh'],
      localizedKeywordsKey: 'slashCommand.keywords.util.reload',
      icon: Refresh,
      run: () => {
        window.location.reload();
      },
    },
    {
      id: 'util.copyUrl',
      title: { key: 'slashCommand.util.copyUrl', value: 'Copy page URL' },
      group: 'utilities',
      keywords: ['copy', 'url', 'link', 'share'],
      localizedKeywordsKey: 'slashCommand.keywords.util.copyUrl',
      icon: LinkIcon,
      run: async ({ closeDialog }) => {
        closeDialog();
        try {
          await navigator.clipboard.writeText(window.location.href);
        } catch {
          // Clipboard may be unavailable (non-HTTPS, iframe, permissions).
          // Nothing to do here — the command is best-effort.
        }
      },
    },
    {
      id: 'util.advancedSearch',
      title: { key: 'slashCommand.util.advancedSearch', value: 'Advanced search' },
      group: 'utilities',
      keywords: ['search', 'find', 'advanced'],
      localizedKeywordsKey: 'slashCommand.keywords.util.advancedSearch',
      icon: SearchIcon,
      run: ({ navigate, closeDialog }) => {
        closeDialog();
        navigate('/search');
      },
    },
    {
      id: 'util.theme.light',
      title: { key: 'slashCommand.util.theme.light', value: 'Theme: Light' },
      group: 'utilities',
      keywords: ['theme', 'light', 'appearance'],
      localizedKeywordsKey: 'slashCommand.keywords.util.theme.light',
      icon: LightMode,
      run: ({ updatePreference, closeDialog }) => {
        closeDialog();
        updatePreference(USER_PREF_KEYS.theme, 'light');
      },
    },
    {
      id: 'util.theme.dark',
      title: { key: 'slashCommand.util.theme.dark', value: 'Theme: Dark' },
      group: 'utilities',
      keywords: ['theme', 'dark', 'appearance'],
      localizedKeywordsKey: 'slashCommand.keywords.util.theme.dark',
      icon: DarkMode,
      run: ({ updatePreference, closeDialog }) => {
        closeDialog();
        updatePreference(USER_PREF_KEYS.theme, 'dark');
      },
    },
    {
      id: 'util.theme.system',
      title: { key: 'slashCommand.util.theme.system', value: 'Theme: System' },
      group: 'utilities',
      keywords: ['theme', 'system', 'auto', 'appearance'],
      localizedKeywordsKey: 'slashCommand.keywords.util.theme.system',
      icon: SettingsBrightness,
      run: ({ updatePreference, closeDialog }) => {
        closeDialog();
        updatePreference(USER_PREF_KEYS.theme, 'system');
      },
    },
  ];

  // Language switcher: one command per supported language.
  for (const lang of SUPPORTED_LANGUAGES) {
    commands.push({
      id: `util.language.${lang}`,
      title: {
        key: `slashCommand.util.language.${lang}`,
        value: `Language: ${lang.toUpperCase()}`,
      },
      group: 'utilities',
      keywords: ['language', 'locale', lang],
      localizedKeywordsKey: `slashCommand.keywords.util.language.${lang}`,
      icon: LanguageIcon,
      run: ({ updatePreference, closeDialog }) => {
        closeDialog();
        updatePreference(USER_PREF_KEYS.language, lang);
      },
    });
  }

  return commands;
}
